/**
 * An inline message about the state of something on the page.
 *
 * The `role` depends on the tone, and getting that wrong is the usual mistake.
 * `alert` interrupts whatever a screen reader is saying; `status` waits for a
 * pause. An error the user must act on earns the interruption. A success
 * confirmation does not — interrupting for "saved" is the assistive-technology
 * equivalent of a modal dialog for every keystroke.
 */

import type { ReactNode } from "react";

import "./feedback.css";

export type AlertTone = "info" | "success" | "warning" | "danger";

export interface AlertProps {
  tone?: AlertTone;
  title?: string;
  /**
   * The body, when there is one.
   *
   * Optional, because a one-line notice is a real shape: "aucun connecteur
   * installé" says everything it has to say in its title. Requiring a body
   * made call sites repeat the title as the content, which a screen reader
   * then announces twice.
   */
  children?: ReactNode;
  /** Rendered at the end — a retry or a link to what to do next. */
  action?: ReactNode;
}

export function Alert({ tone = "info", title, children, action }: AlertProps) {
  return (
    <div
      className="hardauto-alert"
      data-tone={tone}
      role={tone === "danger" ? "alert" : "status"}
      aria-live={tone === "danger" ? "assertive" : "polite"}
    >
      <div className="hardauto-alert__body">
        {title ? <p className="hardauto-alert__title">{title}</p> : null}
        {children ? <div className="hardauto-alert__content">{children}</div> : null}
      </div>
      {action ? <div className="hardauto-alert__action">{action}</div> : null}
    </div>
  );
}
